import {
  Box,
  Button,
  Heading,
  VStack,
  FormErrorMessage,
  useToast,
  FormControl,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { Formik, Field, Form } from "formik";
import { changePasswordPageValidation } from "./changePasswordPageValidation";
import { PasswordInput } from "./passwordInput";
import { updatePassword } from "@/services/user.service";
import { useNavigate } from "react-router-dom";

interface ChangePasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const bgColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const initialValues: ChangePasswordFormValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  const handleSubmit = async (
    values: ChangePasswordFormValues,
    actions: any
  ) => {
    try {
      await updatePassword({
        oldPassword: values.currentPassword,
        password: values.newPassword,
      });

      toast({
        title: "Senha atualizada com sucesso.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/");
    } catch (error) {
      toast({
        title: "Erro ao atualizar a senha.",
        description: "Por favor, tente novamente.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Box
      maxH="550px"
      maxW="500px"
      mx="auto"
      mt={8}
      p={6}
      boxShadow="xl"
      borderRadius="lg"
      bg={bgColor}
      borderWidth={1}
      borderColor={borderColor}
    >
      <VStack spacing={4} align="stretch">
        <Heading as="h2" size="lg" textAlign="center">
          Trocar Senha
        </Heading>
        <Text fontSize="sm" textAlign="center" color="gray.500">
          Mantenha sua conta segura atualizando sua senha regularmente.
        </Text>
        <Formik
          initialValues={initialValues}
          validationSchema={changePasswordPageValidation}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting, isValid, dirty }) => (
            <Form>
              <VStack spacing={4} align="center">
                <Box width="90%">
                  <Field name="currentPassword">
                    {({ field }: any) => (
                      <FormControl
                        isInvalid={
                          !!errors.currentPassword && touched.currentPassword
                        }
                      >
                        <PasswordInput label="Senha Atual" {...field} />
                        <FormErrorMessage>
                          {errors.currentPassword}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Box>

                <Box width="90%">
                  <Field name="newPassword">
                    {({ field }: any) => (
                      <FormControl
                        isInvalid={!!errors.newPassword && touched.newPassword}
                      >
                        <PasswordInput label="Nova Senha" {...field} />
                        <FormErrorMessage>{errors.newPassword}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Box>

                <Box width="90%">
                  <Field name="confirmPassword">
                    {({ field }: any) => (
                      <FormControl
                        isInvalid={
                          !!errors.confirmPassword && touched.confirmPassword
                        }
                      >
                        <PasswordInput label="Confirmar Nova Senha" {...field} />
                        <FormErrorMessage>
                          {errors.confirmPassword}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Box>

                <Button
                  type="submit"
                  backgroundColor="#2b5b83"
                  _hover={{ backgroundColor: "#234b6a" }}
                  color="white"
                  mt={8}
                  width="60%"
                  height="60px"
                  size="md"
                  fontSize="lg"
                  fontWeight="bold"
                  isLoading={isSubmitting}
                  loadingText="Alterando..."
                  boxShadow="md"
                  _active={{ boxShadow: "inner" }}
                  isDisabled={!isValid || !dirty}
                >
                  Alterar Senha
                </Button>
              </VStack>
            </Form>
          )}
        </Formik>
      </VStack>
    </Box>
  );
}
